import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState, useEffect } from 'react';
import './CircularGauge.scss';
export const CircularGauge = ({ size, value, title, description, max, inverted, ranking }) => {
    const valueRef = useRef(inverted ? 1 : max);
    const [currentValue, setCurrentValue] = useState(valueRef.current);
    const animate = () => {
        if (value > 0 && value <= max) {
            const duration = 400;
            let start = null;
            const initialValue = valueRef.current;
            const targetValue = value;
            const direction = initialValue < targetValue ? +1 : -1;
            function step(ts) {
                if (!start)
                    start = ts;
                const progress = Math.min((ts - start) / duration, 1);
                valueRef.current = initialValue + (targetValue - initialValue) * progress;
                setCurrentValue(valueRef.current);
                if (progress < 1) {
                    requestAnimationFrame(step);
                }
                else {
                    valueRef.current = targetValue;
                    setCurrentValue(targetValue);
                }
            }
            requestAnimationFrame(step);
        }
    };
    useEffect(() => {
        animate();
    }, [value]);
    const strokeWidth = size * 0.1;
    const padding = 2;
    const cx = size / 2;
    const cy = cx;
    const r = cx - strokeWidth / 2;
    const circumference = Math.PI * 2 * r;
    const scaleStartOffset = (circumference / 360) * 220;
    const degreesCutoff = 100;
    const scaleMaxLen = (circumference / 360) * (360 - degreesCutoff);
    const dashLen = (scaleMaxLen / max) * (inverted ? max - (currentValue) : (currentValue));
    const gapLen = circumference - dashLen;
    const layerDim = { width: size, height: size };
    const radA = (Math.PI / 180) * (90 + degreesCutoff / 2);
    const cxA = cx + (r * Math.cos(radA));
    const cyA = cy + (r * Math.sin(radA));
    const radB = (Math.PI / 180) * (90 - degreesCutoff / 2);
    const cxB = cx + (r * Math.cos(radB));
    const cyB = cy + (r * Math.sin(radB));
    const clipA = [
        cx + (r + strokeWidth / 2) * Math.cos(radA + 0.001),
        cy + (r + strokeWidth / 2) * Math.sin(radA + 0.001),
        cx + (r - strokeWidth / 2) * Math.cos(radA + 0.001),
        cy + (r - strokeWidth / 2) * Math.sin(radA + 0.001),
        cx + (r - strokeWidth / 2) * Math.cos(radA - 0.15),
        cy + (r - strokeWidth / 2) * Math.sin(radA - 0.15),
        cx + (r + strokeWidth / 2) * Math.cos(radA - 0.15),
        cy + (r + strokeWidth / 2) * Math.sin(radA - 0.15)
    ];
    const clipB = [
        cx + (r + strokeWidth / 2) * Math.cos(radB),
        cy + (r + strokeWidth / 2) * Math.sin(radB),
        cx + (r - strokeWidth / 2) * Math.cos(radB),
        cy + (r - strokeWidth / 2) * Math.sin(radB),
        cx + (r - strokeWidth / 2) * Math.cos(radB + 0.15),
        cy + (r - strokeWidth / 2) * Math.sin(radB + 0.15),
        cx + (r + strokeWidth / 2) * Math.cos(radB + 0.15),
        cy + (r + strokeWidth / 2) * Math.sin(radB + 0.15)
    ];
    const primaryScale = currentValue >= max
        ? _jsx(_Fragment, {})
        : (_jsx(_Fragment, { children: _jsx("circle", { cx: cx, cy: cy, r: r, strokeWidth: strokeWidth, strokeDasharray: `${dashLen - padding} ${gapLen + padding}`, strokeDashoffset: scaleStartOffset, stroke: "#c7cd04", fill: "none" }) }));
    const remainingScale = currentValue <= 1
        ? _jsx(_Fragment, {})
        : (_jsx(_Fragment, { children: _jsx("circle", { cx: cx, cy: cy, r: r, strokeWidth: strokeWidth, strokeDasharray: `${(scaleMaxLen - dashLen) - padding} ${(circumference - (scaleMaxLen - dashLen) + padding)}`, strokeDashoffset: (scaleStartOffset - dashLen) - padding, stroke: "#ffffffaa", fill: "none" }) }));
    return (_jsx("div", { className: "circular gauge", children: _jsxs("figure", { style: layerDim, children: [_jsx("picture", { style: layerDim, children: _jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", version: "1.1", viewBox: `0 0 ${size} ${size}`, children: [_jsxs("defs", { children: [_jsx("clipPath", { id: "clipA", children: _jsx("polygon", { points: `
                    ${clipA[0]} ${clipA[1]}
                    ${clipA[2]} ${clipA[3]}
                    ${clipA[4]} ${clipA[5]}
                    ${clipA[6]} ${clipA[7]}
                  ` }) }), _jsx("clipPath", { id: "clipB", children: _jsx("polygon", { points: `
                    ${clipB[0]} ${clipB[1]}
                    ${clipB[2]} ${clipB[3]}
                    ${clipB[4]} ${clipB[5]}
                    ${clipB[6]} ${clipB[7]}
                  ` }) })] }), primaryScale, _jsx("circle", { cx: cxA, cy: cyA, r: strokeWidth / 2, strokeWidth: 0, fill: "#c7cd04", clipPath: "url(#clipA)" }), remainingScale, _jsx("circle", { cx: cxB, cy: cyB, r: strokeWidth / 2, strokeWidth: 0, fill: "#ffffffaa", clipPath: "url(#clipB)" })] }) }), _jsxs("figcaption", { style: layerDim, children: [_jsx("span", { className: `value ${ranking ? 'ranking' : 'figure'}`, children: value }), _jsx("span", { className: "description", children: description }), _jsx("span", { className: "title", children: title })] })] }) }));
};
